import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";

import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";

import MDBox from "mdComponents/MDBox";
import MDTypography from "mdComponents/MDTypography";
import MDInput from "mdComponents/MDInput";
import MDButton from "mdComponents/MDButton";

import BasicLayoutNew from "layouts/authentication/components/BasicLayoutNew";
import corporateAccountService from "services/corporateAccountService";
import LoadingSpinner from "controls/LoadingSpinner";
import { signUp } from "services/auth";
import { formatPhoneNumber } from "Utils";
import { MESSAGES_ERROR, MESSAGE_AUTH } from "../../constants";
import exceptionService from "services/exceptionService";
import { MainLogo } from "../../components/Icons/MainLogo";
import bgImage from "assets/images/city.jpg";
import {
  authFeatureChipSx,
  authInputSx,
  authPanelSx,
  authSurfaceSx,
  primaryButtonSx,
  secondaryButtonSx,
} from "./authStyles";

type Step1Data = {
  businessName: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
};

type Step2Data = {
  businessPhone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
};

const inputPropsWithIcon = (icon: React.ReactNode) => ({
  startAdornment: <InputAdornment position="start">{icon}</InputAdornment>,
});

export default function SignUpForm(): any {
  const navigate = useNavigate();

  const [step, setStep] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [step1, setStep1] = useState<Step1Data>({
    businessName: "",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [step2, setStep2] = useState<Step2Data>({
    businessPhone: "",
    address: "",
    city: "",
    state: "",
    zip: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [createdEmail, setCreatedEmail] = useState<string>("");

  const validateStep1 = (): boolean => {
    const e: Record<string, string> = {};
    if (!step1.businessName.trim()) e.businessName = "Business name is required";
    if (!step1.firstName.trim()) e.firstName = "First name is required";
    if (!step1.lastName.trim()) e.lastName = "Last name is required";
    if (!step1.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(step1.email)) e.email = "Invalid email";
    if (!step1.password) e.password = "Password is required";
    if (step1.password !== step1.confirmPassword) e.confirmPassword = "Passwords do not match";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = (): boolean => {
    const e: Record<string, string> = {};
    if (!step2.businessPhone.trim()) e.businessPhone = "Business phone is required";
    if (!step2.address.trim()) e.address = "Address is required";
    if (!step2.city.trim()) e.city = "City is required";
    if (!step2.state.trim()) e.state = "State is required";
    if (!step2.zip.trim()) e.zip = "Zip code is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    setErrorMessage("");
    if (step === 1) {
      if (validateStep1()) setStep(2);
    } else {
      createAccount();
    }
  };

  const handleBack = () => {
    setErrorMessage("");
    if (step === 2) setStep(1);
    else navigate("/authentication/sign-in");
  };

  const createAccount = async () => {
    if (!validateStep2()) return;
    setErrorMessage("");
    setLoading(true);
    await registerUserAndSaveCorporateAccount();
  };

  const registerUserAndSaveCorporateAccount = async () => {
    for (let attempt = 1; attempt <= 3; attempt++) {
      const { user, authError } = await signUp(
        step1.email.trim(),
        step1.password.trim(),
        step1.firstName.trim(),
        step1.lastName.trim(),
        formatPhoneNumber(step2.businessPhone),
        step2.address.trim(),
        step2.city.trim(),
        step2.state,
        step2.zip,
        null,
        true
      );

      if (user) {
        setCreatedEmail(step1.email.trim());
        await saveCorporateAccount(user?.id);
        return;
      }

      if (authError) {
        setErrorMessage(
          authError?.message === MESSAGES_ERROR.ERROR_USER_ALREADY_REGISTERED
            ? MESSAGE_AUTH.USER_ALREADY_REGISTERED_MESSAGE
            : authError?.message || MESSAGES_ERROR.ERROR_REGISTER_USER
        );
        setStep(1);
        setLoading(false);
        return;
      }
    }

    setErrorMessage("An unknown error occurred. Please try again.");
    setStep(1);
    setLoading(false);
  };

  const saveCorporateAccount = async (ownerUserId: string) => {
    try {
      const newAccount = {
        businessName: step1.businessName.trim(),
        address: step2.address.trim(),
        city: step2.city.trim(),
        state: step2.state,
        zip: step2.zip.trim(),
        contactEmail: step1.email.trim(),
        contactPhone: step2.businessPhone,
        contactFirstName: step1.firstName,
        contactLastName: step1.lastName,
        notes: "created via web portal sign-up",
        active: true,
        ownerUserId,
      };

      const result = await corporateAccountService.insertCorporateAccount(newAccount);
      if (result !== -1) {
        setSubmitted(true);
      } else {
        setErrorMessage("Unable to save business account. Please try again.");
      }
    } catch (error: any) {
      exceptionService.logError(error, "AddCorporateAccount.doSave", ownerUserId);
      setErrorMessage("There was a problem saving. " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const formInput = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    errorKey: string,
    icon: React.ReactNode,
    type = "text"
  ) => (
    <MDInput
      label={label}
      type={type}
      value={value}
      onChange={(ev: React.ChangeEvent<HTMLInputElement>) => onChange(ev.target.value)}
      fullWidth
      error={!!errors[errorKey]}
      helperText={errors[errorKey]}
      sx={authInputSx}
      InputProps={inputPropsWithIcon(icon)}
    />
  );

  if (submitted) {
    return (
      <BasicLayoutNew image={bgImage}>
        <Card sx={authSurfaceSx}>
          <MDBox p={{ xs: 3, md: 4 }} textAlign="center">
            <MainLogo style={{ width: "220px", maxWidth: "78%" }} />
            <MDBox
              mt={2}
              mx="auto"
              sx={{
                width: 58,
                height: 58,
                borderRadius: "18px",
                backgroundColor: "rgba(46, 174, 31, 0.12)",
                color: "#2eae1f",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 34 }} />
            </MDBox>
            <MDTypography variant="h3" color="dark" fontWeight={900} mt={2}>
              Account created
            </MDTypography>
            <MDTypography variant="body2" color="text" fontWeight="medium" mt={1}>
              A confirmation email has been sent to <strong>{createdEmail}</strong>.
            </MDTypography>
            <MDTypography variant="body2" color="text" mt={2} sx={{ lineHeight: 1.6 }}>
              Please confirm your email address before signing in. You will not be able
              to log in until the account is verified.
            </MDTypography>
            <MDBox mt={3}>
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                onClick={() => navigate("/authentication/sign-in")}
                sx={primaryButtonSx}
              >
                Go to Login
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>
      </BasicLayoutNew>
    );
  }

  return (
    <BasicLayoutNew image={bgImage} wide>
      <Grid container spacing={3} alignItems="stretch" justifyContent="center">
        <Grid item xs={12} lg={7}>
          <Card sx={{ ...authSurfaceSx, height: "100%" }}>
            <MDBox p={{ xs: 3, md: 4 }}>
              <MDBox textAlign="center">
                <MainLogo style={{ width: "220px", maxWidth: "78%" }} />
                <MDTypography variant="h3" color="dark" fontWeight={900} mt={1}>
                  Create your business account
                </MDTypography>
                <MDTypography variant="body2" color="text" fontWeight="medium" mt={1}>
                  {step === 1
                    ? "Enter the details you will use to sign in and manage your host portal."
                    : "Add your business contact and address information."}
                </MDTypography>
              </MDBox>

              <MDBox display="flex" gap={1.25} mt={3} mb={2.5}>
                {[1, 2].map((item) => (
                  <MDBox
                    key={item}
                    flex={1}
                    sx={{
                      height: 8,
                      borderRadius: "999px",
                      backgroundColor: item <= step ? "#0878ed" : "rgba(20, 41, 62, 0.12)",
                    }}
                  />
                ))}
              </MDBox>

              <MDBox component="form">
                {step === 1 && (
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      {formInput(
                        "Business Name",
                        step1.businessName,
                        (businessName) => setStep1({ ...step1, businessName }),
                        "businessName",
                        <BusinessOutlinedIcon fontSize="small" />
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {formInput(
                        "First Name",
                        step1.firstName,
                        (firstName) => setStep1({ ...step1, firstName }),
                        "firstName",
                        <PersonOutlineOutlinedIcon fontSize="small" />
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {formInput(
                        "Last Name",
                        step1.lastName,
                        (lastName) => setStep1({ ...step1, lastName }),
                        "lastName",
                        <PersonOutlineOutlinedIcon fontSize="small" />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      {formInput(
                        "Email",
                        step1.email,
                        (email) => setStep1({ ...step1, email }),
                        "email",
                        <EmailOutlinedIcon fontSize="small" />,
                        "email"
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {formInput(
                        "Password",
                        step1.password,
                        (password) => setStep1({ ...step1, password }),
                        "password",
                        <LockOutlinedIcon fontSize="small" />,
                        "password"
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {formInput(
                        "Confirm Password",
                        step1.confirmPassword,
                        (confirmPassword) => setStep1({ ...step1, confirmPassword }),
                        "confirmPassword",
                        <LockOutlinedIcon fontSize="small" />,
                        "password"
                      )}
                    </Grid>
                  </Grid>
                )}

                {step === 2 && (
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      {formInput(
                        "Business Phone",
                        step2.businessPhone,
                        (businessPhone) => setStep2({ ...step2, businessPhone }),
                        "businessPhone",
                        <PhoneOutlinedIcon fontSize="small" />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      {formInput(
                        "Address",
                        step2.address,
                        (address) => setStep2({ ...step2, address }),
                        "address",
                        <LocationOnOutlinedIcon fontSize="small" />
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {formInput(
                        "City",
                        step2.city,
                        (city) => setStep2({ ...step2, city }),
                        "city",
                        <LocationOnOutlinedIcon fontSize="small" />
                      )}
                    </Grid>
                    <Grid item xs={12} md={3}>
                      {formInput(
                        "State",
                        step2.state,
                        (state) => setStep2({ ...step2, state }),
                        "state",
                        <LocationOnOutlinedIcon fontSize="small" />
                      )}
                    </Grid>
                    <Grid item xs={12} md={3}>
                      {formInput(
                        "Zip Code",
                        step2.zip,
                        (zip) => setStep2({ ...step2, zip }),
                        "zip",
                        <LocationOnOutlinedIcon fontSize="small" />
                      )}
                    </Grid>
                  </Grid>
                )}

                {errorMessage && !loading && (
                  <MDBox mt={2} p={1.5} borderRadius="12px" sx={{ backgroundColor: "rgba(215, 38, 61, 0.10)" }}>
                    <MDTypography variant="body2" color="error" textAlign="center" fontWeight="medium">
                      {errorMessage}
                    </MDTypography>
                  </MDBox>
                )}

                {loading && (
                  <MDBox mt={2}>
                    <LoadingSpinner size={25} title="Working..." />
                  </MDBox>
                )}

                <MDBox display="flex" gap={1.25} mt={3} flexDirection={{ xs: "column", sm: "row" }}>
                  <MDButton
                    variant="outlined"
                    color="dark"
                    fullWidth
                    onClick={handleBack}
                    sx={secondaryButtonSx}
                    startIcon={<ArrowBackOutlinedIcon />}
                    disabled={loading}
                  >
                    {step === 1 ? "Cancel" : "Back"}
                  </MDButton>

                  <MDButton
                    variant="gradient"
                    color={step === 1 ? "info" : "success"}
                    fullWidth
                    onClick={handleNext}
                    sx={primaryButtonSx}
                    endIcon={step === 1 ? <ArrowForwardOutlinedIcon /> : <CheckCircleOutlineOutlinedIcon />}
                    disabled={loading}
                  >
                    {step === 1 ? "Next" : "Create Account"}
                  </MDButton>
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Card sx={{ ...authPanelSx, height: "100%" }}>
            <MDBox p={{ xs: 3, md: 4 }}>
              <MDBox sx={authFeatureChipSx}>
                <VerifiedUserOutlinedIcon sx={{ fontSize: 18 }} />
                Step {step} of 2
              </MDBox>
              <MDTypography
                variant="h3"
                fontWeight={900}
                mt={2}
                mb={1}
                sx={{ color: "#ffffff", lineHeight: 1.12 }}
              >
                {step === 1 ? "Your portal login" : "Your business profile"}
              </MDTypography>
              <MDTypography
                variant="body2"
                sx={{ color: "rgba(248, 251, 255, 0.76)", fontWeight: 500, lineHeight: 1.7 }}
              >
                {step === 1
                  ? "This account becomes the owner login for your host portal."
                  : "This profile helps your team manage host locations, contact details, and access settings."}
              </MDTypography>

              <MDBox mt={3}>
                {[
                  "Secure account setup",
                  "Email verification before login",
                  "Location management after signup",
                ].map((item) => (
                  <MDBox
                    key={item}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    mb={1.5}
                    sx={{
                      p: 1.5,
                      borderRadius: "15px",
                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                      border: "1px solid rgba(255, 255, 255, 0.10)",
                    }}
                  >
                    <CheckCircleOutlineOutlinedIcon sx={{ color: "#6be334", fontSize: 19 }} />
                    <MDTypography variant="button" fontWeight="bold" sx={{ color: "#ffffff" }}>
                      {item}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>

              <MDTypography
                variant="caption"
                sx={{ color: "rgba(248, 251, 255, 0.62)", display: "block", mt: 3, lineHeight: 1.6 }}
              >
                Already registered? Confirm your email, then return to login to enter the host portal.
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </BasicLayoutNew>
  );
}
